import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  GraduationCap, Calendar, Clock, MapPin, Users, DollarSign,
  CheckCircle, ChevronRight, ArrowLeft, AlertCircle, LogIn, UserCheck
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

// Mock class data keyed by classId
const mockClasses = {
  '1': {
    id: 1, className: 'Adult Beginner Tennis', description: 'Perfect for adults new to tennis. Learn basic strokes and court positioning.',
    sportName: 'Tennis', skillLevel: 'beginner', coachName: 'Sarah Johnson',
    recurrence: 'Mon/Wed/Fri', time: '18:00', duration: 90,
    enrolled: 15, maxCapacity: 20, facility: 'Court A',
    enrollmentFee: 150.00, enrollmentDueDate: '2026-03-01',
    startDate: '2026-03-01', endDate: '2026-05-31',
    academyName: 'Elite Tennis Academy', academyId: '1'
  },
  '2': {
    id: 2, className: 'Intermediate Tennis', description: 'Improve your game with advanced techniques and match play.',
    sportName: 'Tennis', skillLevel: 'intermediate', coachName: 'Mike Chen',
    recurrence: 'Tue/Thu', time: '17:00', duration: 90,
    enrolled: 12, maxCapacity: 16, facility: 'Court B',
    enrollmentFee: 0, enrollmentDueDate: null,
    startDate: '2026-03-01', endDate: '2026-05-31',
    academyName: 'Elite Tennis Academy', academyId: '1'
  },
  '3': {
    id: 3, className: 'Pickleball Beginner Bootcamp', description: 'Everything you need to start playing pickleball.',
    sportName: 'Pickleball', skillLevel: 'beginner', coachName: 'Raj Patel',
    recurrence: 'Mon/Wed', time: '09:00', duration: 60,
    enrolled: 8, maxCapacity: 12, facility: 'Court 1',
    enrollmentFee: 80.00, enrollmentDueDate: '2026-06-01',
    startDate: '2026-06-01', endDate: '2026-08-31',
    academyName: 'Pickleball Pro Academy', academyId: '3'
  }
};

const formatTime = (time24) => {
  if (!time24) return '';
  const [h, m] = time24.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${period}`;
};

const SKILL_LEVELS = ['beginner', 'intermediate', 'advanced'];

// ── Logged-in user profile card ────────────────────────────────────────────────
const KynaixProfileCard = ({ user }) => {
  const { theme } = useTheme();
  return (
    <div className={`flex items-center gap-4 p-4 rounded-xl border-2 border-green-400 bg-green-50 dark:bg-green-900/20 mb-6`}>
      <div className="relative flex-shrink-0">
        {user.profileImage ? (
          <img src={user.profileImage} alt={user.firstName} className="w-14 h-14 rounded-full object-cover" />
        ) : (
          <div className="w-14 h-14 rounded-full bg-green-200 flex items-center justify-center">
            <span className="text-green-700 font-bold text-xl">
              {user.firstName?.[0]}{user.lastName?.[0]}
            </span>
          </div>
        )}
        <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
          <UserCheck size={11} className="text-white" />
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <p className="font-semibold text-green-800 dark:text-green-200">
            {user.firstName} {user.lastName}
          </p>
          <span className="px-2 py-0.5 bg-green-500 text-white rounded text-xs font-semibold">Kynaix</span>
        </div>
        <p className="text-sm text-green-700 dark:text-green-300 truncate">{user.email}</p>
        {user.phoneNumber && (
          <p className="text-xs text-green-600 dark:text-green-400">{user.phoneNumber}</p>
        )}
      </div>
      <Link
        to="/profile"
        className="text-xs text-green-600 underline whitespace-nowrap hover:text-green-800 transition"
      >
        Edit profile
      </Link>
    </div>
  );
};

const ClassRegistrationPage = () => {
  const { classId } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { user } = useAuth();

  const cls = mockClasses[classId] || mockClasses['2'];
  const isFull = cls.enrolled >= cls.maxCapacity;
  const isLoggedIn = !!user;

  // For logged-in users there's only 1 step (academy-specific info).
  // For new users there are 2 steps: personal info → emergency contact + terms.
  const totalSteps = isLoggedIn ? 1 : 2;
  const [step, setStep] = useState(1);

  const [formData, setFormData] = useState({
    // Personal info (only collected for new users)
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dob: '',
    gender: '',
    // Academy-specific (collected for everyone)
    skillLevel: cls.skillLevel,
    emergencyContactName: '',
    emergencyContactPhone: '',
    medicalNotes: '',
    agreedToTerms: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  // Step 1 for new users: personal info validation
  const handleStep1Submit = (e) => {
    e.preventDefault();
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      toast.error('Please enter your full name'); return;
    }
    if (!formData.email.trim()) {
      toast.error('Please enter your email address'); return;
    }
    if (!formData.phone.trim()) {
      toast.error('Please enter your phone number'); return;
    }
    setStep(2);
  };

  // Final submit (step 2 for new users, step 1 for logged-in)
  const handleFinalSubmit = (e) => {
    e.preventDefault();
    if (!formData.emergencyContactName.trim()) {
      toast.error('Please provide an emergency contact name'); return;
    }
    if (!formData.emergencyContactPhone.trim()) {
      toast.error('Please provide an emergency contact phone'); return;
    }
    if (!formData.agreedToTerms) {
      toast.error('Please agree to the terms and conditions'); return;
    }
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setDone(true);
    }, 1200);
  };

  const spotsLeft = cls.maxCapacity - cls.enrolled;

  // ── Class Info Card ─────────────────────────────────────────────────────────
  const ClassInfoCard = () => (
    <div className={`${theme.card} rounded-xl p-6 mb-6 border ${theme.border}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className={`text-sm ${theme.textSecondary} mb-1`}>{cls.academyName}</p>
          <h2 className={`text-xl font-bold ${theme.text}`}>{cls.className}</h2>
        </div>
        <span className={`px-3 py-1 rounded-lg text-xs font-semibold capitalize ${
          cls.skillLevel === 'beginner' ? 'bg-green-100 text-green-700' :
          cls.skillLevel === 'intermediate' ? 'bg-yellow-100 text-yellow-700' :
          'bg-red-100 text-red-700'
        }`}>
          {cls.skillLevel}
        </span>
      </div>
      <p className={`text-sm ${theme.textSecondary} mb-4`}>{cls.description}</p>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div className={`flex items-center gap-2 ${theme.text}`}>
          <Calendar size={15} className="text-blue-500 flex-shrink-0" />
          <span>{cls.recurrence}</span>
        </div>
        <div className={`flex items-center gap-2 ${theme.text}`}>
          <Clock size={15} className="text-blue-500 flex-shrink-0" />
          <span>{formatTime(cls.time)} · {cls.duration} min</span>
        </div>
        <div className={`flex items-center gap-2 ${theme.text}`}>
          <MapPin size={15} className="text-blue-500 flex-shrink-0" />
          <span>{cls.facility}</span>
        </div>
        <div className={`flex items-center gap-2 ${isFull ? 'text-red-600' : spotsLeft <= 3 ? 'text-orange-600' : theme.text}`}>
          <Users size={15} className="flex-shrink-0" />
          <span>{isFull ? 'Class full' : `${spotsLeft} spot${spotsLeft !== 1 ? 's' : ''} left`}</span>
        </div>
      </div>
      {cls.enrollmentFee > 0 && (
        <div className="mt-4 flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200">
          <DollarSign size={16} className="text-blue-600" />
          <span className="font-semibold text-blue-700">${cls.enrollmentFee.toFixed(2)}</span>
          <span className={`text-sm ${theme.textSecondary}`}>enrollment fee</span>
          {cls.enrollmentDueDate && (
            <span className="ml-auto text-xs text-orange-600 flex items-center gap-1">
              <AlertCircle size={13} />
              Due {new Date(cls.enrollmentDueDate).toLocaleDateString()}
            </span>
          )}
        </div>
      )}
    </div>
  );

  // ── Academy-specific fields (shown for everyone) ────────────────────────────
  const AcademyFields = ({ isSubmitStep }) => (
    <div className="space-y-4">
      <div>
        <label className={`block text-sm font-medium ${theme.text} mb-1`}>Skill Level</label>
        <div className="flex gap-2">
          {SKILL_LEVELS.map(level => (
            <button
              key={level} type="button"
              onClick={() => setFormData(prev => ({ ...prev, skillLevel: level }))}
              className={`flex-1 py-2 rounded-lg text-sm font-medium capitalize border transition ${
                formData.skillLevel === level
                  ? level === 'beginner' ? 'bg-green-500 text-white border-green-500'
                    : level === 'intermediate' ? 'bg-yellow-500 text-white border-yellow-500'
                    : 'bg-red-500 text-white border-red-500'
                  : `${theme.border} ${theme.text} ${theme.cardHover}`
              }`}
            >
              {level}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={`block text-sm font-medium ${theme.text} mb-1`}>Emergency Contact Name *</label>
          <input
            type="text" name="emergencyContactName" value={formData.emergencyContactName}
            onChange={handleChange} placeholder="Full name"
            className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
            required={isSubmitStep}
          />
        </div>
        <div>
          <label className={`block text-sm font-medium ${theme.text} mb-1`}>Emergency Contact Phone *</label>
          <input
            type="tel" name="emergencyContactPhone" value={formData.emergencyContactPhone}
            onChange={handleChange} placeholder="+1 (555) 000-0000"
            className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
            required={isSubmitStep}
          />
        </div>
      </div>

      <div>
        <label className={`block text-sm font-medium ${theme.text} mb-1`}>Medical Notes / Special Requirements</label>
        <textarea
          name="medicalNotes" value={formData.medicalNotes} onChange={handleChange}
          placeholder="Any injuries, allergies, or accommodations the coach should know about..."
          rows={3}
          className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none`}
        />
      </div>

      <div className={`p-4 rounded-lg border ${theme.border} ${theme.cardHover}`}>
        <h4 className={`font-semibold text-sm ${theme.text} mb-2`}>Terms & Conditions</h4>
        <p className={`text-xs ${theme.textSecondary} leading-relaxed`}>
          By enrolling, you agree to attend classes regularly and notify the academy at least 24 hours before an
          absence. Enrollment fees are non-refundable after the class start date. The academy reserves the right
          to remove participants who do not follow the code of conduct.
        </p>
      </div>

      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox" name="agreedToTerms" checked={formData.agreedToTerms} onChange={handleChange}
          className="mt-0.5 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
        <span className={`text-sm ${theme.text}`}>
          I agree to the terms and conditions above and confirm the information provided is accurate.
          {!isLoggedIn && (
            <span className="text-blue-600"> A Kynaix Sports account will be created for me using the email above.</span>
          )}
        </span>
      </label>
    </div>
  );

  return (
    <div className={`${theme.background} min-h-screen`}>
      {/* Top bar */}
      <div className={`${theme.card} border-b px-4 py-3 flex items-center gap-3`}>
        <button onClick={() => navigate(-1)} className={`p-2 rounded-full ${theme.cardHover} transition-colors`}>
          <ArrowLeft size={20} className={theme.text} />
        </button>
        <GraduationCap size={22} className="text-blue-500" />
        <span className={`font-semibold ${theme.text}`}>Class Registration</span>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        <ClassInfoCard />

        {/* Full class warning */}
        {isFull && !done && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 rounded-xl flex items-center gap-3">
            <AlertCircle size={20} className="text-red-500 flex-shrink-0" />
            <div>
              <p className="font-semibold text-red-700">This class is full</p>
              <p className="text-sm text-red-600">You can still join the waitlist below.</p>
            </div>
          </div>
        )}

        {/* ── LOGGED-IN FLOW ── single step */}
        {isLoggedIn && !done && (
          <div className={`${theme.card} rounded-xl p-6 border ${theme.border}`}>
            <h3 className={`text-lg font-semibold ${theme.text} mb-1`}>Complete your registration</h3>
            <p className={`text-sm ${theme.textSecondary} mb-5`}>
              We already have your Kynaix profile. Just fill in a few academy-specific details.
            </p>

            <KynaixProfileCard user={user} />

            <form onSubmit={handleFinalSubmit} className="space-y-5">
              <AcademyFields isSubmitStep />

              <button
                type="submit"
                disabled={submitting || !formData.agreedToTerms}
                className={`w-full py-3 ${theme.buttonPrimary} rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2`}
              >
                {submitting ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Submitting...</>
                ) : (
                  isFull ? 'Join Waitlist' : 'Submit Registration'
                )}
              </button>
            </form>
          </div>
        )}

        {/* ── NEW USER FLOW ── step 1: personal info */}
        {!isLoggedIn && !done && step === 1 && (
          <div className={`${theme.card} rounded-xl p-6 border ${theme.border}`}>
            {/* Sign-in prompt */}
            <div className="flex items-center justify-between mb-5 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                Already have a Kynaix account?
              </p>
              <Link
                to={`/signin?redirect=/register/class/${classId}`}
                className="flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-800 transition"
              >
                <LogIn size={15} />
                Sign in
              </Link>
            </div>

            <h3 className={`text-lg font-semibold ${theme.text} mb-1`}>Your Information</h3>
            <p className={`text-sm ${theme.textSecondary} mb-5`}>
              You'll automatically get a Kynaix Sports account to track your classes and progress.
            </p>

            <form onSubmit={handleStep1Submit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium ${theme.text} mb-1`}>First Name *</label>
                  <input type="text" name="firstName" value={formData.firstName} onChange={handleChange}
                    placeholder="First name"
                    className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    required />
                </div>
                <div>
                  <label className={`block text-sm font-medium ${theme.text} mb-1`}>Last Name *</label>
                  <input type="text" name="lastName" value={formData.lastName} onChange={handleChange}
                    placeholder="Last name"
                    className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    required />
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium ${theme.text} mb-1`}>Email Address *</label>
                <input type="email" name="email" value={formData.email} onChange={handleChange}
                  placeholder="your@email.com"
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  required />
                <p className={`text-xs ${theme.textSecondary} mt-1`}>Your Kynaix account will be sent to this email.</p>
              </div>

              <div>
                <label className={`block text-sm font-medium ${theme.text} mb-1`}>Phone Number *</label>
                <input type="tel" name="phone" value={formData.phone} onChange={handleChange}
                  placeholder="+1 (555) 000-0000"
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  required />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium ${theme.text} mb-1`}>Date of Birth</label>
                  <input type="date" name="dob" value={formData.dob} onChange={handleChange}
                    className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent`} />
                </div>
                <div>
                  <label className={`block text-sm font-medium ${theme.text} mb-1`}>Gender</label>
                  <select name="gender" value={formData.gender} onChange={handleChange}
                    className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}>
                    <option value="">Prefer not to say</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <button type="submit"
                className={`w-full py-3 ${theme.buttonPrimary} rounded-lg font-semibold flex items-center justify-center gap-2`}>
                Continue <ChevronRight size={18} />
              </button>
            </form>
          </div>
        )}

        {/* ── NEW USER FLOW ── step 2: emergency contact + terms */}
        {!isLoggedIn && !done && step === 2 && (
          <div className={`${theme.card} rounded-xl p-6 border ${theme.border}`}>
            {/* Summary of what was entered */}
            <div className={`flex items-center gap-3 p-3 rounded-lg ${theme.cardHover} border ${theme.border} mb-5`}>
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <span className="text-blue-700 font-bold text-sm">
                  {formData.firstName[0]}{formData.lastName[0]}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className={`font-semibold text-sm ${theme.text}`}>{formData.firstName} {formData.lastName}</p>
                <p className={`text-xs ${theme.textSecondary} truncate`}>{formData.email}</p>
              </div>
              <button onClick={() => setStep(1)} className={`text-xs ${theme.textSecondary} hover:underline`}>Edit</button>
            </div>

            <h3 className={`text-lg font-semibold ${theme.text} mb-1`}>Class Details</h3>
            <p className={`text-sm ${theme.textSecondary} mb-5`}>
              A few more details for the academy.
            </p>

            <form onSubmit={handleFinalSubmit} className="space-y-5">
              <AcademyFields isSubmitStep />

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setStep(1)}
                  className={`flex-1 py-3 border ${theme.border} rounded-lg ${theme.text} ${theme.cardHover} transition font-medium`}>
                  Back
                </button>
                <button type="submit"
                  disabled={submitting || !formData.agreedToTerms}
                  className={`flex-1 py-3 ${theme.buttonPrimary} rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2`}>
                  {submitting ? (
                    <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Submitting...</>
                  ) : (
                    isFull ? 'Join Waitlist' : 'Register & Create Account'
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* ── SUCCESS ── */}
        {done && (
          <div className={`${theme.card} rounded-xl p-8 border ${theme.border} text-center`}>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} className="text-green-600" />
            </div>
            <h3 className={`text-2xl font-bold ${theme.text} mb-2`}>
              {isFull ? 'Added to Waitlist!' : 'Registration Submitted!'}
            </h3>
            <p className={`${theme.textSecondary} mb-4`}>
              {isFull
                ? `You're on the waitlist for ${cls.className}.`
                : `Your registration for ${cls.className} has been submitted.`}
              {' '}The academy will confirm at{' '}
              <strong>{isLoggedIn ? user.email : formData.email}</strong>.
            </p>

            {/* New user: account creation notice */}
            {!isLoggedIn && (
              <div className="mb-5 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 text-left">
                <p className="font-semibold text-blue-700 mb-1 flex items-center gap-2">
                  <UserCheck size={16} />
                  Kynaix Sports account created
                </p>
                <p className="text-sm text-blue-600">
                  A Kynaix account has been created for <strong>{formData.email}</strong>.
                  Check your inbox for login details so you can track your classes, attendance, and progress.
                </p>
              </div>
            )}

            {!isLoggedIn && cls.enrollmentFee > 0 && (
              <div className="mb-5 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 rounded-lg text-left">
                <p className="text-sm font-semibold text-yellow-700 mb-1">Payment Required</p>
                <p className="text-sm text-yellow-600">
                  Please arrange payment of <strong>${cls.enrollmentFee.toFixed(2)}</strong> to complete your enrollment.
                  {cls.enrollmentDueDate && ` Due by ${new Date(cls.enrollmentDueDate).toLocaleDateString()}.`}
                </p>
              </div>
            )}

            <div className="flex gap-3">
              {isLoggedIn ? (
                <>
                  <button onClick={() => navigate('/academies')}
                    className={`flex-1 py-3 border ${theme.border} rounded-lg ${theme.text} ${theme.cardHover} transition font-medium`}>
                    Browse Academies
                  </button>
                  <button onClick={() => navigate('/profile')}
                    className={`flex-1 py-3 ${theme.buttonPrimary} rounded-lg font-semibold`}>
                    My Profile
                  </button>
                </>
              ) : (
                <>
                  <button onClick={() => navigate('/academies')}
                    className={`flex-1 py-3 border ${theme.border} rounded-lg ${theme.text} ${theme.cardHover} transition font-medium`}>
                    Browse Academies
                  </button>
                  <Link to="/signin"
                    className={`flex-1 py-3 ${theme.buttonPrimary} rounded-lg font-semibold flex items-center justify-center gap-2`}>
                    <LogIn size={16} />
                    Sign In
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClassRegistrationPage;
